import React, { useState, useEffect } from "react";
import { debounce } from "lodash";
import {
  formatCurrency,
  formatCurrencyInput,
  processCurrencyInput,
  formatCurrencyOnBlur,
} from "../../utils/formatters";
import logger from '../../utils/logger';
import api from "../../utils/apiConfig";
import IncomeSummaryCard from "./IncomeSummaryCard";
import { useTheme } from "../../contexts/ThemeContext";
import { CARD_BASE } from '../../constants/styles';

// Custom SVG icons
const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const PieChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
  </svg>
);

const IncomeAnalysisTable = ({
  incomes = [],
  members = [],
  onAddIncome,
  onEditIncome,
  onDeleteIncome,
  isEditMode = false,
  getIncomeTypeName = (id) => `Type ${id}`,
  getIncomeTypeId = () => null,
  onSave,
  onCancel,
  onViewDetails,
}) => {
  const { isDarkMode } = useTheme();
  const [incomeTypes, setIncomeTypes] = useState({});
  const [frequencies, setFrequencies] = useState([]);
  const [localIncomes, setLocalIncomes] = useState(incomes);
  const [showEditPanel, setShowEditPanel] = useState(false);

  // Update local incomes when prop changes
  useEffect(() => {
    setLocalIncomes(incomes);
  }, [incomes]);

  // Handle income updates
  const handleIncomeUpdate = (updatedIncome) => {
    // Update local state
    setLocalIncomes(prevIncomes => 
      prevIncomes.map(income => 
        income.id === updatedIncome.id ? updatedIncome : income
      )
    );
    // Propagate change to parent
    onEditIncome?.(updatedIncome);
  };

  // Handle adding new income
  const handleAddIncome = () => {
    console.log('handleAddIncome called');
    // Create new income with default values
    const newIncome = {
      id: `temp_${Date.now()}`, // Temporary ID until saved
      member_id: "",            // Default to household
      income_type_id: "",       // Empty until selected
      income_type_name: "",     // Empty until selected
      frequency_id: "",         // Empty until selected
      frequency_name: "",       // Empty until selected
      frequency_multiplier: 1,  // Default multiplier
      amount: 0,               // Default amount
      gross_amount: 0,         // Default gross amount
    };

    console.log('Creating new income:', newIncome);
    
    // Update local state first
    setLocalIncomes(currentIncomes => {
      const newIncomes = [...currentIncomes, newIncome];
      console.log('Updated localIncomes:', newIncomes);
      return newIncomes;
    });

    // Then propagate to parent
    onAddIncome?.(newIncome);
  };

  // Handle deleting income
  const handleDeleteIncome = (incomeToDelete) => {
    console.log('Deleting income:', incomeToDelete);

    // Update local state first
    setLocalIncomes(currentIncomes => {
      const newIncomes = currentIncomes.filter(income => income.id !== incomeToDelete.id);
      console.log('Updated localIncomes after delete:', newIncomes);
      return newIncomes;
    });

    // Then propagate to parent
    onDeleteIncome?.(incomeToDelete);
  };

  // Fetch reference data including frequencies and income types
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        logger.info("Fetching reference data for income analysis...");
        const response = await api.get("/advisor-portal/reference-data");
        logger.info("Income reference data response:", response.data);

        if (response.data && response.data.incomeTypes) {
          const typesMap = response.data.incomeTypes.reduce((acc, type) => {
            acc[type.income_type_id] = {
              income_type_id: type.income_type_id,
              income_type_name: type.income_type_name,
              category: type.category,
              behavior: type.behavior,
            };
            return acc;
          }, {});
          logger.info("Income types map:", typesMap);
          setIncomeTypes(typesMap);
        }

        if (response.data && response.data.frequencies) {
          logger.info("Frequencies:", response.data.frequencies);
          setFrequencies(response.data.frequencies);
        } else {
          logger.warn("No frequencies found in reference data, using defaults");
          // Set default frequencies if none are returned
          setFrequencies([
            {
              frequency_id: 1,
              frequency_name: "Annual",
              frequency_multiplier: 1,
            },
            {
              frequency_id: 2,
              frequency_name: "Monthly",
              frequency_multiplier: 12,
            },
            {
              frequency_id: 3,
              frequency_name: "Bi-Weekly",
              frequency_multiplier: 26,
            },
            {
              frequency_id: 4,
              frequency_name: "Quarterly",
              frequency_multiplier: 4,
            },
            {
              frequency_id: 5,
              frequency_name: "Semi-Annual",
              frequency_multiplier: 2,
            },
            {
              frequency_id: 6,
              frequency_name: "Weekly",
              frequency_multiplier: 52,
            },
            {
              frequency_id: 7,
              frequency_name: "One-Time",
              frequency_multiplier: 1,
            },
          ]);
        }
      } catch (error) {
        logger.error("Error fetching income reference data:", error);
      }
    };

    fetchReferenceData();
  }, []);

  // Normalize incomeTypes to always be an object
  const normalizedIncomeTypes = React.useMemo(() => {
    if (Array.isArray(incomeTypes)) {
      return incomeTypes.reduce((acc, type) => {
        if (type?.income_type_id) {
          acc[type.income_type_id] = type;
        }
        return acc;
      }, {});
    }
    return incomeTypes || {};
  }, [incomeTypes]);

  // Normalize frequencies to always be an array
  const normalizedFrequencies = React.useMemo(() => {
    return Array.isArray(frequencies) ? frequencies : Object.values(frequencies || {});
  }, [frequencies]);

  // Debug logs
  console.log('Normalized Data:', {
    incomeTypes: normalizedIncomeTypes,
    frequencies: normalizedFrequencies,
  });

  // Group incomes by member with proper household handling
  const groupedIncomes = React.useMemo(() => {
    return (localIncomes || []).reduce((acc, income) => {
      const isMemberIncome = (members || []).some(
        (member) => member?.id === income?.member_id,
      );
      const targetId = isMemberIncome ? income.member_id : "household";

      if (!acc[targetId]) {
        acc[targetId] = [];
      }
      acc[targetId].push(income);
      return acc;
    }, {});
  }, [localIncomes, members]);

  // Calculate totals with proper household handling
  const totals = React.useMemo(() => {
    const memberTotals = {};
    let householdTotal = 0;
    let grandTotal = 0;

    // Calculate member totals first
    (members || []).forEach((member) => {
      if (!member) return;
      const memberIncomes = groupedIncomes[member.id] || [];
      const total = memberIncomes.reduce((sum, income) => {
        const annualAmount =
          parseFloat(income?.gross_amount || income?.amount || 0) *
          (income?.frequency_multiplier || 1);
        return sum + annualAmount;
      }, 0);
      memberTotals[member.id] = total;
      grandTotal += total;
    });

    // Calculate household total separately
    const householdIncomes = groupedIncomes.household || [];
    householdTotal = householdIncomes.reduce((sum, income) => {
      const annualAmount =
        parseFloat(income?.gross_amount || income?.amount || 0) *
        (income?.frequency_multiplier || 1);
      return sum + annualAmount;
    }, 0);
    grandTotal += householdTotal;

    return { memberTotals, householdTotal, grandTotal };
  }, [groupedIncomes, members]);

  // Prepare data for the summary card
  const incomeSummaryData = React.useMemo(() => {
    console.log('Recalculating incomeSummaryData with localIncomes:', localIncomes);
    
    // Group incomes by type
    const incomesByType = (localIncomes || []).reduce((acc, income) => {
      if (!income) return acc;
      const typeName = income.income_type_name || getIncomeTypeName(income.income_type_id);
      const annualAmount = parseFloat(income?.gross_amount || income?.amount || 0) * (income?.frequency_multiplier || 1);
      
      if (!acc[typeName]) {
        acc[typeName] = {
          name: typeName,
          value: 0,
          details: []
        };
      }
      
      acc[typeName].value += annualAmount;
      acc[typeName].details.push({
        ...income,
        memberName: (members || []).find(m => m?.id === income?.member_id)?.name || 'Household'
      });
      
      return acc;
    }, {});

    // Convert to array and calculate percentages
    const chartData = Object.values(incomesByType).map(item => ({
      ...item,
      percentage: ((item.value / (totals.grandTotal || 1)) * 100).toFixed(1),
      displayValue: formatCurrency(item.value)
    }));

    const summaryData = {
      chartData,
      detailedIncomes: localIncomes,
      totalAnnualIncome: formatCurrency(totals.grandTotal)
    };

    console.log('Calculated summaryData:', summaryData);
    return summaryData;
  }, [localIncomes, members, getIncomeTypeName, totals.grandTotal]);

  return (
    <div className="w-full relative">
      {/* Clickable Summary Card with accent line */}
      <div
        className={`${CARD_BASE} group flex h-full relative cursor-pointer hover:shadow-lg transition-shadow outline-none focus:ring-2 focus:ring-green-600`}
        tabIndex={0}
        aria-label="Edit income details"
        onClick={() => setShowEditPanel(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setShowEditPanel(true);
          }
        }}
        role="button"
      >
        {/* Green accent line (left) */}
        <div className="absolute left-0 top-0 h-full w-1 rounded-l-lg bg-green-600 group-hover:bg-green-700 transition-colors"></div>
        <div className="flex-1">
          <IncomeSummaryCard
            {...incomeSummaryData}
            incomeTypes={incomeTypes}
            frequencies={frequencies}
            onEditIncome={handleIncomeUpdate}
            onDeleteIncome={handleDeleteIncome}
            onAddIncome={handleAddIncome}
            onSave={onSave}
            onCancel={onCancel}
            members={members}
            getIncomeTypeName={getIncomeTypeName}
          />
        </div>
      </div>

      {/* Edit Modal */}
    </div>
  );
};

export default IncomeAnalysisTable;
